/**
 * RumahKira API Service
 * Handles all communication with the PHP backend
 */

// API Base URL - Change this to your production URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost/rumahkira/backend/api';

// Storage keys
const TOKEN_KEY = 'rumahkira_auth_token';

/**
 * Get stored auth token
 */
export const getToken = () => {
  return localStorage.getItem(TOKEN_KEY);
};

/**
 * Set auth token
 */
export const setToken = (token) => {
  localStorage.setItem(TOKEN_KEY, token);
};

/**
 * Remove auth token
 */
export const removeToken = () => {
  localStorage.removeItem(TOKEN_KEY);
};

/**
 * Make API request
 */
const apiRequest = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;

  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  // Add auth token if available
  const token = getToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  try {
    const response = await fetch(url, {
      ...options,
      headers,
    });

    const data = await response.json();

    if (!response.ok) {
      throw new ApiError(data.message || 'Request failed', response.status, data.errors, data.error_code);
    }

    return data;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(error.message || 'Network error', 0);
  }
};

/**
 * Custom API Error class
 */
export class ApiError extends Error {
  constructor(message, status, errors = [], error_code = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.errors = errors;
    this.error_code = error_code;
  }
}

// ============================================
// AUTH API
// ============================================

/**
 * Register new user
 */
export const registerUser = async (name, email, password, phone = null) => {
  const ref = localStorage.getItem('rumahkira_referral_code');
  const response = await apiRequest('/auth/register.php', {
    method: 'POST',
    body: JSON.stringify({ name, email, password, phone, ...(ref && { ref }) }),
  });

  if (response.success && response.data.token) {
    setToken(response.data.token);
    localStorage.removeItem('rumahkira_referral_code');
  }

  return response;
};

/**
 * Login user
 */
export const loginUser = async (email, password) => {
  const response = await apiRequest('/auth/login.php', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });

  if (response.success && response.data.token) {
    setToken(response.data.token);
  }

  return response;
};

/**
 * Get user profile
 */
export const getProfile = async () => {
  return await apiRequest('/auth/profile.php', {
    method: 'GET',
  });
};

/**
 * Update user profile
 */
export const updateProfile = async (data) => {
  return await apiRequest('/auth/profile.php', {
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

/**
 * Logout user
 */
export const logoutUser = () => {
  removeToken();
};

// ============================================
// PROJECTS API
// ============================================

/**
 * Save project
 */
export const saveProject = async (projectData) => {
  return await apiRequest('/projects/save.php', {
    method: 'POST',
    body: JSON.stringify(projectData),
  });
};

/**
 * Get all projects
 */
export const getProjects = async (page = 1, limit = 20) => {
  return await apiRequest(`/projects/list.php?page=${page}&limit=${limit}`, {
    method: 'GET',
  });
};

/**
 * Get single project
 */
export const getProject = async (projectId) => {
  return await apiRequest(`/projects/list.php?id=${projectId}`, {
    method: 'GET',
  });
};

/**
 * Delete project
 */
export const deleteProject = async (projectId) => {
  return await apiRequest(`/projects/delete.php?id=${projectId}`, {
    method: 'DELETE',
  });
};

// ============================================
// PRICES API
// ============================================

/**
 * Get all material prices, cost rates, house types
 */
export const getAllPrices = async () => {
  return await apiRequest('/prices/get-all.php', {
    method: 'GET',
  });
};

// ============================================
// SUBSCRIPTION API
// ============================================

/**
 * Get subscription plans
 */
export const getSubscriptionPlans = async () => {
  return await apiRequest('/subscription/plans.php', {
    method: 'GET',
  });
};

/**
 * Get user subscription status
 */
export const getSubscriptionStatus = async () => {
  return await apiRequest('/subscription/status.php', {
    method: 'GET',
  });
};

/**
 * Record feature usage
 */
export const useFeature = async (featureCode, metadata = null) => {
  return await apiRequest('/subscription/use-feature.php', {
    method: 'POST',
    body: JSON.stringify({ feature: featureCode, metadata }),
  });
};

/**
 * Redeem a code for Access Pass
 */
export const redeemCode = async (code) => {
  return await apiRequest('/subscription/redeem.php', {
    method: 'POST',
    body: JSON.stringify({ code }),
  });
};

// ============================================
// PAYMENT API
// ============================================

/**
 * Create payment for subscription
 */
export const createPayment = async (planCode) => {
  return await apiRequest('/payment/create.php', {
    method: 'POST',
    body: JSON.stringify({ plan: planCode }),
  });
};

/**
 * Verify payment status
 */
export const verifyPayment = async (reference, billCode = null) => {
  const params = new URLSearchParams();
  if (reference) params.append('ref', reference);
  if (billCode) params.append('billcode', billCode);

  return await apiRequest(`/payment/verify.php?${params.toString()}`, {
    method: 'GET',
  });
};

// ============================================
// PASSWORD RESET API
// ============================================

/**
 * Request password reset email
 */
export const forgotPassword = async (email) => {
  return await apiRequest('/auth/forgot-password.php', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
};

/**
 * Reset password with token
 */
export const resetPassword = async (token, password) => {
  return await apiRequest('/auth/reset-password.php', {
    method: 'POST',
    body: JSON.stringify({ token, password }),
  });
};

// ============================================
// REFERRAL API
// ============================================

/**
 * Get user's referral code and share links
 */
export const getReferralCode = async () => {
  return await apiRequest('/referral/code.php', { method: 'GET' });
};

/**
 * Get user's referral stats
 */
export const getReferralStats = async () => {
  return await apiRequest('/referral/stats.php', { method: 'GET' });
};

// ============================================
// PROMO API
// ============================================

/**
 * Get latest active promo code
 */
export const getLatestPromo = async () => {
  return await apiRequest('/promo/latest.php', { method: 'GET' });
};

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Check if user is authenticated (has valid token)
 */
export const isAuthenticated = () => {
  return !!getToken();
};

/**
 * Format API error for display
 */
export const formatApiError = (error) => {
  if (error instanceof ApiError) {
    return error.message;
  }
  return 'An unexpected error occurred. Please try again.';
};

export default {
  // Auth
  registerUser,
  loginUser,
  logoutUser,
  getProfile,
  updateProfile,
  isAuthenticated,

  // Password Reset
  forgotPassword,
  resetPassword,

  // Projects
  saveProject,
  getProjects,
  getProject,
  deleteProject,

  // Prices
  getAllPrices,

  // Subscription
  getSubscriptionPlans,
  getSubscriptionStatus,
  useFeature,
  redeemCode,

  // Payment
  createPayment,
  verifyPayment,

  // Referral
  getReferralCode,
  getReferralStats,

  // Promo
  getLatestPromo,

  // Utilities
  getToken,
  setToken,
  removeToken,
  formatApiError,
  ApiError,
};
